import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import {
  FileText, Upload, LayoutDashboard, Bell, Trash2, AlertCircle,
  CheckCircle2, Clock, TrendingUp, RefreshCw, ArrowLeft, Edit3,
  Save, X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, ShieldCheck,
  Activity, Download, Calendar, Sliders, Users, Link2, Network,
  DollarSign, GitBranch, AlertTriangle, Sparkles, Lightbulb, Send, HelpCircle
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { humanLabel, humanizeText } from "../i18n/ctLabels";

// pdf.js worker: servito da /public/pdf.worker.min.mjs (copiato in build)
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contract-twin`;

const CLAUSE_COLORS = {
  renewal: "#00E5FF",
  price_revision: "#FFB800",
  penalty: "#FF1744",
  termination: "#7C3AED",
  parity_clause: "#00E676",
};
const TRIGGER_VAR_OPTIONS = ["ecb_rate", "fixed_date", "other"];

export default function ContractTwin() {
  const { t  } = useLanguage();
  const labels = {
    renewal: t("ct.clause.renewal"),
    price_revision: t("ct.clause.price_revision"),
    penalty: t("ct.clause.penalty"),
    termination: t("ct.clause.termination"),
    parity_clause: t("ct.clause.parity_clause"),
  };

  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const fileRef = useRef(null);
  const [me, setMe] = useState(null);
  const [contractTypes, setContractTypes] = useState([]);
  const [uploadType, setUploadType] = useState("other");
  const [filterType, setFilterType] = useState("all");

  const loadDash = useCallback(async () => {
    try { const r = await axios.get(`${API}/dashboard`, { withCredentials: true }); setDash(r.data); } catch { /* noop */ }
  }, []);
  const loadContracts = useCallback(async () => {
    try { const r = await axios.get(`${API}/contracts`, { withCredentials: true }); setContracts(r.data); } catch { /* noop */ }
  }, []);
  const loadAlerts = useCallback(async () => {
    try { const r = await axios.get(`${API}/alerts`, { withCredentials: true }); setAlerts(r.data); } catch { /* noop */ }
  }, []);
  const loadMe = useCallback(async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setMe(r.data);
    } catch { /* noop */ }
  }, []);
  const loadContractTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contract-types`, { withCredentials: true });
      setContractTypes(r.data);
      // Default upload type: first enabled
      const firstEnabled = r.data.find(x => x.enabled && x.id !== "other");
      if (firstEnabled) setUploadType(firstEnabled.id);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadDash(); loadContracts(); loadAlerts(); loadMe(); loadContractTypes(); }, [loadDash, loadContracts, loadAlerts, loadMe, loadContractTypes]);

  const handleFiles = async (files) => {
    setUploadError("");
    setUploading(true);
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      form.append("contract_type", uploadType);
      try {
        await axios.post(`${API}/contracts/upload`, form, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      } catch (e) {
        setUploadError(e?.response?.data?.detail || (t("ct.upload.failed")));
      }
    }
    setUploading(false);
    loadDash(); loadContracts();
  };

  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };
  const onSelect = (e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ""; };

  const removeContract = async (id) => {
    const msg = t("ct.confirm_delete");
    if (!window.confirm(msg)) return;
    try { await axios.delete(`${API}/contracts/${id}`, { withCredentials: true }); loadDash(); loadContracts(); loadAlerts(); }
    catch { /* noop */ }
  };

  // Detail view
  if (selectedId) {
    return (
      <ContractDetail
        contractId={selectedId}
        onBack={() => { setSelectedId(null); loadDash(); loadContracts(); }}
        labels={labels}
      />
    );
  }

  const isAdmin = me?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0A0C10] text-white p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-[#00E5FF]/12" style={{ borderRadius: "2px" }}>
          <FileText className="w-5 h-5 text-[#00E5FF]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Contract Twin</h1>
          <p className="text-xs text-[#A0AEC0] tracking-wide">{t("ct.subtitle")}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/[0.04]">
        {[
          { k: "dashboard", label: t("ct.tab.dashboard"), icon: LayoutDashboard },
          { k: "contracts", label: t("ct.tab.contracts"), icon: FileText },
          { k: "whatif", label: t("ct.tab.whatif"), icon: Sliders },
          { k: "counterparties", label: t("ct.tab.counterparties"), icon: Users },
          { k: "predictions", label: t("ct.tab.predictions"), icon: TrendingUp },
          { k: "recommendations", label: t("ct.tab.recommendations"), icon: Lightbulb },
          { k: "analytics", label: t("ct.tab.analytics"), icon: Activity },
          { k: "alerts", label: t("ct.tab.alerts"), icon: Bell },
        ].map(t2 => (
          <button
            key={t2.k}
            data-testid={`ct-tab-${t2.k}`}
            onClick={() => setTab(t2.k)}
            className={`flex items-center gap-2 px-4 py-2 text-[12px] tracking-widest uppercase font-semibold transition-colors ${
              tab === t2.k ? "text-[#00E5FF] border-b-2 border-[#00E5FF]" : "text-[#A0AEC0] hover:text-white"
            }`}
          >
            <t2.icon className="w-3.5 h-3.5" /> {t2.label}
            {t2.k === "alerts" && dash?.alerts_active > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-[#FF1744] text-white" style={{ borderRadius: "2px" }} data-testid="ct-alerts-badge">{dash.alerts_active}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "dashboard" && dash && (
        <div className="space-y-4">
          <NLQueryBar onOpenContract={(id) => setSelectedId(id)} />
          <div className="flex justify-end">
            <ReportDownloadButton />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label={t("ct.stat.contracts")} value={dash.contracts_total} color="#00E5FF" icon={FileText} testid="ct-stat-contracts" />
            <StatCard label={t("ct.stat.clauses")} value={dash.clauses_total} color="#7C3AED" icon={CheckCircle2} testid="ct-stat-clauses" />
            <StatCard label={t("ct.stat.review")} value={dash.clauses_needs_review} color="#FFB800" icon={AlertCircle} onClick={() => setTab("contracts")} testid="ct-stat-review" />
            <StatCard label={t("ct.stat.alerts")} value={dash.alerts_active} color="#FF1744" icon={Bell} onClick={() => setTab("alerts")} testid="ct-stat-alerts" />
            <StatCard label={t("ct.stat.ecb")} value={`${dash.ecb_rate?.current_value ?? "—"}%`} color="#00E676" icon={TrendingUp} testid="ct-stat-rate" />
          </div>

          <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">
              {t("ct.by_type")}
            </p>
            <div className="space-y-2">
              {Object.entries(dash.clauses_by_type).map(([type, n]) => {
                const max = Math.max(1, ...Object.values(dash.clauses_by_type));
                const pct = (n / max) * 100;
                return (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-xs text-[#CBD5E1] w-32 tracking-wide">{labels[type] || type}</span>
                    <div className="flex-1 h-2 bg-white/[0.04]" style={{ borderRadius: "2px" }}>
                      <div style={{ width: `${pct}%`, height: "100%", backgroundColor: CLAUSE_COLORS[type], borderRadius: "2px", transition: "width 300ms" }} />
                    </div>
                    <span className="text-sm font-bold tabular-nums w-8 text-right" style={{ color: CLAUSE_COLORS[type], fontFamily: "Chivo, sans-serif" }}>{n}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {isAdmin && (
            <RateAdminPanel
              current={dash.ecb_rate?.current_value ?? 4.0}
              onUpdated={() => { loadDash(); loadAlerts(); }}
            />
          )}

          {isAdmin && contractTypes.length > 0 && (
            <ContractTypeAdminPanel types={contractTypes} onChanged={loadContractTypes} />
          )}
        </div>
      )}

      {tab === "contracts" && (
        <div className="space-y-4">
          {/* Contract type selector for upload */}
          <div className="bg-[#0D0F12] border border-white/[0.04] p-3" style={{ borderRadius: "2px" }} data-testid="ct-upload-type-picker">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-2">{t("ct.upload.type_label")}</p>
            <div className="flex flex-wrap gap-1.5">
              {contractTypes.filter(ct => ct.enabled).map(ct => (
                <button
                  key={ct.id}
                  onClick={() => setUploadType(ct.id)}
                  className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors ${uploadType === ct.id ? "border-[#00E5FF] bg-[#00E5FF]/12 text-[#00E5FF]" : "border-white/[0.06] text-[#A0AEC0] hover:border-white/[0.15] hover:text-white"}`}
                  style={{ borderRadius: "2px" }}
                  data-testid={`ct-upload-type-${ct.id}`}
                >
                  {t(`ct.type.${ct.id}`)}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#6B7280] mt-2">{t("ct.upload.type_hint")}</p>
          </div>

          <div
            data-testid="ct-uploader"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${uploading ? "opacity-60" : "hover:bg-white/[0.02]"}`}
            style={{ borderColor: "#00E5FF55", borderRadius: "2px" }}
          >
            <input ref={fileRef} type="file" accept="application/pdf" multiple hidden onChange={onSelect} data-testid="ct-file-input" />
            <Upload className="w-8 h-8 text-[#00E5FF] mx-auto mb-2" />
            <p className="text-sm text-white">{uploading
              ? (t("ct.upload.processing"))
              : (t("ct.upload.drop"))}</p>
            <p className="text-[10px] text-[#A0AEC0] mt-1">
              {t("ct.upload.hint")}
            </p>
            {uploadError && <p className="text-[11px] text-[#FF1744] mt-2" data-testid="ct-upload-error">{uploadError}</p>}
          </div>

          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
            <div className="flex items-center px-4 py-2.5 gap-2 flex-wrap">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">{t("ct.list.title")}</p>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="ml-2 bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-[10px] text-white uppercase tracking-widest"
                style={{ borderRadius: "2px" }}
                data-testid="ct-list-filter-type"
              >
                <option value="all">{t("ct.list.all_types")}</option>
                {contractTypes.map(ct => (
                  <option key={ct.id} value={ct.id}>{t(`ct.type.${ct.id}`)}</option>
                ))}
              </select>
              <button onClick={loadContracts} className="ml-auto text-[10px] text-[#A0AEC0] hover:text-white flex items-center gap-1" data-testid="ct-refresh-list">
                <RefreshCw className="w-3 h-3" /> {t("ct.list.refresh")}
              </button>
            </div>
            {contracts.length === 0 ? (
              <p className="text-center text-[11px] text-[#6B7280] py-8">
                {t("ct.list.empty")}
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] tracking-widest uppercase text-[#6B7280]">
                    <th className="text-left py-2 px-4">{t("ct.list.filename")}</th>
                    <th className="text-left py-2 px-2">{t("ct.list.date")}</th>
                    <th className="text-left py-2 px-2">{t("ct.contract.type")}</th>
                    <th className="text-left py-2 px-2">{t("ct.list.status")}</th>
                    <th className="text-right py-2 px-2">{t("ct.list.clauses")}</th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.filter(c => filterType === "all" || (c.contract_type || "other") === filterType).map(c => (
                    <tr key={c.id} data-testid={`ct-row-${c.id}`} className="border-t border-white/[0.03] hover:bg-white/[0.02] cursor-pointer" onClick={() => setSelectedId(c.id)}>
                      <td className="py-2.5 px-4 text-white font-medium truncate max-w-[300px]" title={c.filename}>{c.filename}</td>
                      <td className="py-2.5 px-2 text-[#A0AEC0]">{c.upload_date?.slice(0, 10)}</td>
                      <td className="py-2.5 px-2 text-[10px] text-[#B39BFF] uppercase tracking-widest" data-testid={`ct-row-type-${c.id}`}>{t(`ct.type.${c.contract_type || "other"}`)}</td>
                      <td className="py-2.5 px-2"><StatusBadge status={c.status} /></td>
                      <td className="py-2.5 px-2 text-right font-bold tabular-nums text-[#00E5FF]">{c.clauses_count}</td>
                      <td className="py-2.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => removeContract(c.id)}
                          className="text-[#A0AEC0] hover:text-[#FF1744]"
                          data-testid={`ct-delete-${c.id}`}
                          title={t("common.delete")}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === "alerts" && (
        <AlertsPanel alerts={alerts} labels={labels} onOpen={(id) => setSelectedId(id)} onRefresh={() => { loadAlerts(); loadDash(); }} />
      )}

      {tab === "analytics" && (
        <AnalyticsPanel labels={labels} />
      )}

      {tab === "whatif" && (
        <WhatIfPanel labels={labels} onOpenContract={(id) => setSelectedId(id)} />
      )}

      {tab === "counterparties" && (
        <CounterpartiesPanel labels={labels} onOpenContract={(id) => setSelectedId(id)} />
      )}

      {tab === "predictions" && (
        <PredictionsPanel labels={labels} onOpenContract={(id) => setSelectedId(id)} />
      )}

      {tab === "recommendations" && (
        <RecommendationsPanel />
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, onClick, testid }) {
  const { t } = useLanguage();
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`text-left bg-[#0D0F12] border border-white/[0.04] p-4 ${onClick ? "hover:bg-[#12161C] cursor-pointer" : "cursor-default"} transition-colors`}
      style={{ borderRadius: "2px" }}
    >
      <div className="flex items-start justify-between">
        <p className="text-[9px] tracking-[0.22em] uppercase text-[#A0AEC0] font-semibold">{label}</p>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <p className="text-2xl font-bold tabular-nums mt-1" style={{ fontFamily: "Chivo, sans-serif", color }}>{value}</p>
    </button>
  );
}

function StatusBadge({ status }) {
  const { t } = useLanguage();
  if (status === "processed") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00E676] uppercase tracking-widest"><CheckCircle2 className="w-3 h-3" />{t("ct.status.processed")}</span>;
  if (status === "error") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FF1744] uppercase tracking-widest"><AlertCircle className="w-3 h-3" />{t("ct.status.error")}</span>;
  if (status === "processing") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00E5FF] uppercase tracking-widest"><Clock className="w-3 h-3 animate-pulse" />{t("ct.status.processing")}</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FFB800] uppercase tracking-widest"><Clock className="w-3 h-3" />{t("ct.status.pending")}</span>;
}

/* ────────────────────────────────────────────────────────────
   Rate admin panel (visible only to admins)
   ──────────────────────────────────────────────────────────── */
function RateAdminPanel({ current, onUpdated }) {
  const { t, lang } = useLanguage();
  const [val, setVal] = useState(current);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { setVal(current); }, [current]);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const r = await axios.put(`${API}/rate`, { current_value: parseFloat(val) }, { withCredentials: true });
      setMsg(lang === "en"
        ? `Rate updated · ${r.data.alerts_generated} new alerts`
        : `Tasso aggiornato · ${r.data.alerts_generated} nuovi alert`);
      onUpdated?.();
    } catch (e) {
      setMsg(e?.response?.data?.detail || (t("ct.rate.update_failed")));
    } finally { setSaving(false); }
  };

  const syncEcb = async () => {
    setSyncing(true);
    setMsg("");
    try {
      const r = await axios.post(`${API}/rate/sync`, {}, { withCredentials: true });
      setVal(r.data.current_value);
      setMsg(lang === "en"
        ? `Synced from ECB (${r.data.source_period}) · ${r.data.current_value}% · ${r.data.alerts_generated} new alerts`
        : `Sincronizzato da BCE (${r.data.source_period}) · ${r.data.current_value}% · ${r.data.alerts_generated} nuovi alert`);
      onUpdated?.();
    } catch (e) {
      setMsg(e?.response?.data?.detail || (t("ct.rate.sync_failed")));
    } finally { setSyncing(false); }
  };

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-rate-admin-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#00E676]" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E676] font-bold">
          {t("ct.rate.admin_title")}
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="number"
          step="0.05"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="bg-[#0A0C10] border border-white/[0.06] px-3 py-1.5 text-sm w-32 text-white focus:outline-none focus:border-[#00E5FF]"
          style={{ borderRadius: "2px" }}
          data-testid="ct-rate-input"
        />
        <span className="text-[10px] text-[#A0AEC0]">%</span>
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#00E676] text-black px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-[#00E5FF] transition-colors disabled:opacity-50"
          style={{ borderRadius: "2px" }}
          data-testid="ct-rate-save"
        >
          {saving ? (t("ct.rate.saving")) : (t("ct.rate.save"))}
        </button>
        <button
          onClick={syncEcb}
          disabled={syncing}
          className="bg-transparent border border-[#00E5FF] text-[#00E5FF] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-[#00E5FF]/12 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          style={{ borderRadius: "2px" }}
          data-testid="ct-rate-sync"
          title="ECB Data Portal — Main Refinancing Rate (fixed)"
        >
          <Download className="w-3 h-3" />
          {syncing ? (t("ct.rate.syncing")) : (t("ct.rate.sync"))}
        </button>
        {msg && <span className="text-[11px] text-[#A0AEC0]" data-testid="ct-rate-msg">{msg}</span>}
      </div>
      <p className="text-[10px] text-[#6B7280] mt-2">
        {t("ct.rate.hint")}
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Contract type admin panel (visible only to admins)
   ──────────────────────────────────────────────────────────── */
function ContractTypeAdminPanel({ types, onChanged }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const toggle = async (ctype, next) => {
    setBusy(ctype);
    setErr("");
    try {
      await axios.put(`${API}/contract-types/${ctype}/toggle`, { enabled: next }, { withCredentials: true });
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-admin-types-panel">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-[#7C3AED]" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C3AED] font-bold">{t("ct.admin.types_title")}</p>
      </div>
      <p className="text-[10px] text-[#6B7280] mb-3">{t("ct.admin.types_hint")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {types.map(ct => {
          const isLocked = ct.id === "other";
          const isBusy = busy === ct.id;
          return (
            <div key={ct.id} className="flex items-center justify-between bg-[#0A0C10] border border-white/[0.04] px-3 py-2" style={{ borderRadius: "2px" }} data-testid={`ct-admin-type-row-${ct.id}`}>
              <div>
                <p className="text-xs font-bold text-white">{t(`ct.type.${ct.id}`)}</p>
                <p className="text-[10px] text-[#6B7280] uppercase tracking-widest">
                  {isLocked ? t("ct.admin.locked_other") : (ct.enabled ? t("ct.admin.enabled") : t("ct.admin.disabled"))}
                </p>
              </div>
              <button
                disabled={isLocked || isBusy}
                onClick={() => toggle(ct.id, !ct.enabled)}
                data-testid={`ct-admin-toggle-${ct.id}`}
                className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-40 ${
                  ct.enabled
                    ? "border-[#00E676] text-[#00E676] hover:bg-[#00E676]/10"
                    : "border-white/[0.15] text-[#A0AEC0] hover:text-white hover:border-white/[0.3]"
                }`}
                style={{ borderRadius: "2px" }}
              >
                {isBusy ? "…" : (ct.enabled ? t("ct.admin.enabled") : t("ct.admin.disabled"))}
              </button>
            </div>
          );
        })}
      </div>
      {err && <p className="text-[11px] text-[#FF1744] mt-2" data-testid="ct-admin-types-err">{err}</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Alerts panel
   ──────────────────────────────────────────────────────────── */
function AlertsPanel({ alerts, labels, onOpen, onRefresh }) {
  const { t } = useLanguage();
  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/alerts/${id}`, { status }, { withCredentials: true });
      onRefresh?.();
    } catch { /* noop */ }
  };
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
      <div className="px-4 py-2.5 flex items-center">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">
          {t("ct.alerts.title")}
        </p>
        <button onClick={onRefresh} className="ml-auto text-[10px] text-[#A0AEC0] hover:text-white flex items-center gap-1" data-testid="ct-alerts-refresh">
          <RefreshCw className="w-3 h-3" /> {t("ct.list.refresh")}
        </button>
      </div>
      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <Bell className="w-8 h-8 text-[#6B7280] mx-auto mb-2" />
          <p className="text-[11px] text-[#6B7280]">
            {t("ct.alerts.empty")}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-white/[0.03]">
          {alerts.map(a => {
            const direct = a.direct_contract_ids || (a.contract_id ? [a.contract_id] : []);
            const indirect = a.indirect_contract_ids || [];
            const isCP = a.alert_type === "counterparty_risk";
            return (
              <li key={a.id} className="px-4 py-3 flex items-center gap-3" data-testid={`ct-alert-${a.id}`}>
                <Bell className={`w-4 h-4 flex-shrink-0 ${a.status === "active" ? "text-[#FF1744]" : "text-[#6B7280]"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 mr-2 ${isCP ? "bg-[#FF7043]/15 text-[#FF7043]" : "bg-[#FFB800]/15 text-[#FFB800]"}`} style={{ borderRadius: "2px" }} data-testid={`ct-alert-type-${a.id}`}>
                      {isCP ? t("ct.alerts.type_counterparty") : t("ct.alerts.type_market")}
                    </span>
                    <span className="text-[#FFB800] font-bold">{a.counterparty_name || humanLabel(t, "variable", a.variable_name) || humanLabel(t, "clause_type", a.clause_type)}</span>
                    <span className="text-[#A0AEC0] font-normal"> · {(direct.length + indirect.length)} {t("ct.alerts.contracts")} ({direct.length}/{indirect.length} {t("ct.alerts.direct_indirect")})</span>
                  </p>
                  <p className="text-[10px] text-[#A0AEC0]">
                    {isCP ? (<>{t("ct.alerts.status")}: <span className="text-[#FF1744] font-bold">{humanLabel(t, "risk_status", a.risk_status)}</span></>) : (<>{t("ct.alerts.trigger")}: <span className="text-white">{a.triggered_value}</span> ≥ {a.threshold_value}</>)}
                    {" · "}{a.generated_at?.slice(0, 10)}
                    {a.total_exposure_eur != null && (
                      <span className="ml-2">
                        · {t("ct.alerts.exposure")}: <span className="text-[#FF1744] font-bold tabular-nums" data-testid={`ct-alert-exposure-${a.id}`}>€{Number(a.total_exposure_eur).toLocaleString("it-IT")}</span>
                      </span>
                    )}
                  </p>
                </div>
                {a.contract_id && (
                  <button
                    onClick={() => onOpen(a.contract_id)}
                    className="text-[10px] text-[#00E5FF] hover:text-white tracking-widest uppercase"
                    data-testid={`ct-alert-open-${a.id}`}
                  >
                    {t("ct.alerts.open")}
                  </button>
                )}
                <button
                  onClick={() => setStatus(a.id, a.status === "active" ? "resolved" : "active")}
                  className={`text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 ${a.status === "active" ? "text-[#FF1744] bg-[#FF1744]/12 hover:bg-[#FF1744]/20" : "text-[#00E676] bg-[#00E676]/12 hover:bg-[#00E676]/20"}`}
                  data-testid={`ct-alert-toggle-${a.id}`}
                >
                  {a.status === "active" ? (t("ct.alerts.resolve")) : (t("ct.alerts.reopen"))}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Analytics Panel — Recharts aggregations
   ──────────────────────────────────────────────────────────── */
function AnalyticsPanel({ labels }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/analytics`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Errore"));
  }, []);

  if (error) return <p className="text-[11px] text-[#FF1744]" data-testid="ct-analytics-error">{error}</p>;
  if (!data) return <p className="text-[11px] text-[#6B7280] py-8 text-center">{t("ct.loading")}</p>;

  const typeData = data.by_type.map(x => ({ ...x, label: labels[x.type] || x.type, color: CLAUSE_COLORS[x.type] || "#00E5FF" }));
  const reviewData = data.by_review.map(x => ({
    name: x.status === "verified" ? (t("ct.badge.verified")) : (t("ct.badge.review")),
    value: x.count,
  }));
  const confBands = { high: t("ct.analytics.confidence_high"), medium: t("ct.analytics.confidence_medium"), low: t("ct.analytics.confidence_low") };
  const confData = data.by_confidence.map(x => ({ name: confBands[x.band] || x.band, value: x.count }));
  const CONF_COLORS = ["#00E676", "#FFB800", "#FF1744"];
  const REVIEW_COLORS = ["#00E676", "#FFB800"];

  const totalsBlocks = [
    { label: t("ct.stat.contracts"), value: data.totals.contracts, color: "#00E5FF" },
    { label: t("ct.list.clauses"), value: data.totals.clauses, color: "#7C3AED" },
    { label: t("ct.stat.alerts"), value: data.totals.alerts_active, color: "#FF1744" },
    { label: t("ct.analytics.ecb_exposed"), value: data.totals.ecb_exposed_clauses, color: "#FFB800" },
  ];

  return (
    <div className="space-y-4" data-testid="ct-analytics-panel">
      {/* Totals band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {totalsBlocks.map((b, i) => (
          <div key={i} className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
            <p className="text-[9px] tracking-[0.22em] uppercase text-[#A0AEC0] font-semibold">{b.label}</p>
            <p className="text-2xl font-bold tabular-nums mt-1" style={{ fontFamily: "Chivo, sans-serif", color: b.color }}>{b.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Bar chart clausole per tipo */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-analytics-by-type">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">
            {t("ct.analytics.by_type")}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={typeData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#1a2028" />
              <XAxis dataKey="label" tick={{ fill: "#A0AEC0", fontSize: 10 }} />
              <YAxis tick={{ fill: "#A0AEC0", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0C10", border: "1px solid #1a2028", fontSize: 11 }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {typeData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie confidenza */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-analytics-confidence">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">
            {t("ct.analytics.confidence")}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={confData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {confData.map((e, i) => <Cell key={i} fill={CONF_COLORS[i]} stroke="#0A0C10" />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A0C10", border: "1px solid #1a2028", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#A0AEC0" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Timeline scadenze per mese */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-analytics-timeline">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">
            {t("ct.analytics.timeline")}
          </p>
          {data.timeline.length === 0 ? (
            <p className="text-[11px] text-[#6B7280] py-6 text-center">
              <Calendar className="w-6 h-6 mx-auto mb-2 opacity-40" />
              {t("ct.analytics.timeline_empty")}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.timeline} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#1a2028" />
                <XAxis dataKey="month" tick={{ fill: "#A0AEC0", fontSize: 10 }} />
                <YAxis tick={{ fill: "#A0AEC0", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0A0C10", border: "1px solid #1a2028", fontSize: 11 }} />
                <Line type="monotone" dataKey="deadlines" stroke="#00E5FF" strokeWidth={2} dot={{ fill: "#00E5FF", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie review status */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-analytics-review">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">
            {t("ct.analytics.review")}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={reviewData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {reviewData.map((e, i) => <Cell key={i} fill={REVIEW_COLORS[i]} stroke="#0A0C10" />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A0C10", border: "1px solid #1a2028", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#A0AEC0" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ECB exposure list */}
      {data.ecb_exposure.length > 0 && (
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-analytics-ecb">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#FFB800] font-bold mb-3">
            {t("ct.analytics.ecb_title")}
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] tracking-widest uppercase text-[#6B7280]">
                <th className="text-left py-1 pr-3">Tipo</th>
                <th className="text-right py-1 pr-3">{t("ct.field.threshold")}</th>
                <th className="text-left py-1">{t("ct.field.consequence")}</th>
              </tr>
            </thead>
            <tbody>
              {data.ecb_exposure.map(e => (
                <tr key={e.clause_id} className="border-t border-white/[0.03]">
                  <td className="py-1.5 pr-3" style={{ color: CLAUSE_COLORS[e.clause_type] }}>{labels[e.clause_type] || e.clause_type}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-white">{e.threshold_value}%</td>
                  <td className="py-1.5 text-[#CBD5E1] truncate max-w-[420px]">{e.consequence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContractDetail({ contractId, onBack, labels }) {
  const { t } = useLanguage();
  const [contract, setContract] = useState(null);
  const [clauses, setClauses] = useState([]);
  const [otherContracts, setOtherContracts] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [negotiationClauseId, setNegotiationClauseId] = useState(null);
  const [negotiationImpact, setNegotiationImpact] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const r = await axios.get(`${API}/contracts/${contractId}`, { withCredentials: true, signal });
      setContract(r.data.contract);
      setClauses(r.data.clauses);
    } catch (e) {
      if (e?.name === "CanceledError") return;
      setError(e?.response?.data?.detail || "Errore caricamento");
    }
  }, [contractId]);

  const loadOtherContracts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contracts`, { withCredentials: true });
      setOtherContracts(r.data.filter(c => c.id !== contractId));
    } catch { /* noop */ }
  }, [contractId]);

  const loadPdf = useCallback(async (signal) => {
    try {
      const r = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        withCredentials: true,
        responseType: "arraybuffer",
        signal,
      });
      setPdfBytes(new Uint8Array(r.data));
    } catch (e) {
      if (e?.name === "CanceledError") return;
      setError("PDF non disponibile");
    }
  }, [contractId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    loadPdf(controller.signal);
    loadOtherContracts();
    return () => controller.abort();
  }, [load, loadPdf, loadOtherContracts]);

  // Wrap in stable object per react-pdf; use bytes length as key seed
  const pdfFile = useMemo(() => (pdfBytes ? { data: pdfBytes } : null), [pdfBytes]);

  const pdfOptions = useMemo(() => ({
    isEvalSupported: false,
  }), []);

  const grouped = useMemo(() => {
    const isNda = (contract?.contract_type || "other") === "nda";
    const g = { renewal: [], price_revision: [], penalty: [], termination: [], parity_clause: [] };
    for (const c of clauses) {
      // NDA context: hide price_revision, penalty economic, parity_clause per user request
      if (isNda && (c.clause_type === "price_revision" || c.clause_type === "parity_clause")) continue;
      (g[c.clause_type] || (g[c.clause_type] = [])).push(c);
    }
    return g;
  }, [clauses, contract?.contract_type]);

  const saveClause = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/clauses/${id}`, patch, { withCredentials: true });
      setClauses(prev => prev.map(c => c.id === id ? { ...c, ...r.data } : c));
      setEditingId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Salvataggio fallito");
    }
  };

  const saveContractMeta = async (patch) => {
    try {
      const r = await axios.patch(`${API}/contracts/${contractId}`, patch, { withCredentials: true });
      setContract(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || t("ct.edit.save_failed"));
    }
  };

  const reprocessWithType = async (newType) => {
    try {
      const r = await axios.post(`${API}/contracts/${contractId}/reprocess`, { contract_type: newType }, { withCredentials: true });
      setContract(r.data.contract);
      setClauses(r.data.clauses);
    } catch (e) {
      alert(e?.response?.data?.detail || t("ct.edit.save_failed"));
    }
  };

  const runNegotiationImpact = async (clauseId) => {
    setNegotiationClauseId(clauseId);
    setNegotiationImpact(null);
    try {
      const r = await axios.post(`${API}/contracts/${contractId}/negotiation-impact`, { clause_id: clauseId }, { withCredentials: true });
      setNegotiationImpact(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0C10] text-white p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="text-[#A0AEC0] hover:text-white flex items-center gap-2" data-testid="ct-detail-back">
          <ArrowLeft className="w-4 h-4" /> <span className="text-xs tracking-widest uppercase">{t("ct.detail.back")}</span>
        </button>
        <div className="h-4 w-px bg-white/[0.08]" />
        <FileText className="w-4 h-4 text-[#00E5FF]" />
        <h2 className="text-base font-bold truncate max-w-[500px]" title={contract?.filename} style={{ fontFamily: "Chivo, sans-serif" }}>
          {contract?.filename || "…"}
        </h2>
        {contract && <StatusBadge status={contract.status} />}
        <span className="ml-auto text-[10px] text-[#A0AEC0]">
          {clauses.length} {t("ct.detail.clauses")} · {clauses.filter(c => c.review_status === "needs_review").length} {t("ct.detail.to_review")}
        </span>
      </div>

      {error && <div className="text-[11px] text-[#FF1744] mb-3" data-testid="ct-detail-error">{error}</div>}

      {contract && (
        <ContractMetaBar contract={contract} onSave={saveContractMeta} onReprocess={reprocessWithType} />
      )}

      {(contract?.contract_type || "other") === "nda" && (
        <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/40 p-3 mb-4 text-[11px] text-[#B39BFF]" style={{ borderRadius: "2px" }} data-testid="ct-nda-notice">
          {t("ct.nda.hidden_notice")}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* PDF viewer */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-3" style={{ borderRadius: "2px" }} data-testid="ct-pdf-viewer">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))} className="text-[#A0AEC0] hover:text-white" title={t("ct.pdf.zoom_out")} data-testid="ct-pdf-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-[10px] text-[#A0AEC0] tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(2.5, s + 0.1))} className="text-[#A0AEC0] hover:text-white" title={t("ct.pdf.zoom_in")} data-testid="ct-pdf-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <div className="h-4 w-px bg-white/[0.08] mx-1" />
            <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1} className="text-[#A0AEC0] hover:text-white disabled:opacity-30" data-testid="ct-pdf-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[10px] text-[#A0AEC0] tabular-nums" data-testid="ct-pdf-page-indicator">{pageNumber} / {numPages || "—"}</span>
            <button onClick={() => setPageNumber(p => (numPages ? Math.min(numPages, p + 1) : p + 1))} disabled={numPages ? pageNumber >= numPages : false} className="text-[#A0AEC0] hover:text-white disabled:opacity-30" data-testid="ct-pdf-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-[#0A0C10] p-3 flex items-start justify-center overflow-auto" style={{ maxHeight: "calc(100vh - 220px)", borderRadius: "2px" }}>
            {pdfFile ? (
              <Document
                file={pdfFile}
                options={pdfOptions}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(e) => setError("PDF error: " + e.message)}
                loading={<p className="text-[#A0AEC0] text-xs p-4">{t("ct.pdf.loading")}</p>}
              >
                <Page pageNumber={pageNumber} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            ) : (
              <p className="text-[#6B7280] text-xs p-8">{t("ct.pdf.loading")}</p>
            )}
          </div>
        </div>

        {/* Clauses list */}
        <div className="space-y-3" style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }} data-testid="ct-clauses-list">
          {Object.keys(grouped).map(ctype => (
            grouped[ctype].length > 0 && (
              <div key={ctype}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5" style={{ backgroundColor: CLAUSE_COLORS[ctype] }} />
                  <p className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: CLAUSE_COLORS[ctype] }}>
                    {labels[ctype] || ctype} · {grouped[ctype].length}
                  </p>
                </div>
                <div className="space-y-2">
                  {grouped[ctype].map(cl => (
                    editingId === cl.id
                      ? <ClauseEditor key={cl.id} clause={cl} labels={labels} otherContracts={otherContracts} onCancel={() => setEditingId(null)} onSave={(patch) => saveClause(cl.id, patch)} />
                      : <ClauseCard key={cl.id} clause={cl} labels={labels} contracts={otherContracts} onEdit={() => setEditingId(cl.id)} />
                  ))}
                </div>
              </div>
            )
          ))}
          {clauses.length === 0 && (
            <p className="text-[11px] text-[#6B7280] py-8 text-center">
              {t("ct.detail.no_clauses")}
            </p>
          )}
        </div>
      </div>

      {/* Negotiation Impact section (P6) — hidden for NDA */}
      {(contract?.contract_type || "other") !== "nda" && (
        <NegotiationImpactSection
          clauses={clauses}
          selectedClauseId={negotiationClauseId}
          onSelect={runNegotiationImpact}
          impact={negotiationImpact}
          onOpenContract={(id) => window.location.hash = `#c=${id}`}
        />
      )}
    </div>
  );
}

function ContractMetaBar({ contract, onSave, onReprocess }) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(contract.contract_value ?? "");
  const [name, setName] = useState(contract.counterparty_name || "");
  const [ctype, setCtype] = useState(contract.contract_type || "other");
  const [types, setTypes] = useState([]);
  const [reprocessing, setReprocessing] = useState(false);
  useEffect(() => { setVal(contract.contract_value ?? ""); setName(contract.counterparty_name || ""); setCtype(contract.contract_type || "other"); }, [contract.contract_value, contract.counterparty_name, contract.contract_type]);
  useEffect(() => { axios.get(`${API}/contract-types`, { withCredentials: true }).then(r => setTypes(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    const patch = {};
    if (val !== "" && val !== null && val !== undefined) patch.contract_value = parseFloat(val);
    if (name !== null) patch.counterparty_name = name;
    if (Object.keys(patch).length > 0) await onSave(patch);
    // If contract_type changed, trigger reprocess
    if (ctype && ctype !== (contract.contract_type || "other") && onReprocess) {
      setReprocessing(true);
      await onReprocess(ctype);
      setReprocessing(false);
    }
    setEditing(false);
  };

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] p-3 mb-4 flex items-center gap-4 flex-wrap" style={{ borderRadius: "2px" }} data-testid="ct-contract-meta-bar">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#B39BFF]" />
        <span className="text-[10px] uppercase tracking-widest text-[#A0AEC0]">{t("ct.contract.type")}:</span>
        {editing ? (
          <select value={ctype} onChange={e => setCtype(e.target.value)}
            className="bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white"
            style={{ borderRadius: "2px" }}
            data-testid="ct-contract-type-select"
          >
            {types.map(tp => (
              <option key={tp.id} value={tp.id} disabled={!tp.enabled && tp.id !== (contract.contract_type || "other")}>
                {t(`ct.type.${tp.id}`)}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm font-bold text-[#B39BFF]" data-testid="ct-contract-type-display">{t(`ct.type.${contract.contract_type || "other"}`)}</span>
        )}
      </div>
      {(contract.contract_type || "other") !== "nda" && (
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#00E5FF]" />
          <span className="text-[10px] uppercase tracking-widest text-[#A0AEC0]">{t("ct.contract.value")}:</span>
          {editing ? (
            <input type="number" step="1000" value={val} onChange={e => setVal(e.target.value)} className="bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white w-32" style={{ borderRadius: "2px" }} data-testid="ct-contract-value-input" />
          ) : (
            <span className="text-sm font-bold tabular-nums text-white" data-testid="ct-contract-value-display">
              {contract.contract_value != null ? `€${Number(contract.contract_value).toLocaleString("it-IT")}` : "—"}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-1 min-w-[240px]">
        <Users className="w-4 h-4 text-[#7C3AED]" />
        <span className="text-[10px] uppercase tracking-widest text-[#A0AEC0]">{t("ct.contract.counterparty")}:</span>
        {editing ? (
          <input type="text" value={name} onChange={e => setName(e.target.value)} className="bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white flex-1" style={{ borderRadius: "2px" }} data-testid="ct-contract-counterparty-input" />
        ) : (
          <span className="text-sm text-white flex items-center gap-2" data-testid="ct-contract-counterparty-display">
            {contract.counterparty_name || "—"}
            {contract.counterparty_name && (
              <a
                href={`/entity-loss-simulation?name=${encodeURIComponent(contract.counterparty_name)}`}
                className="text-[9px] tracking-widest uppercase font-bold text-[#FF1744] hover:text-white border border-[#FF1744]/40 hover:border-white px-1.5 py-0.5 transition-colors"
                style={{ borderRadius: "2px" }}
                title={t("el.simulate")}
                data-testid="ct-simulate-loss-btn"
              >
                {t("el.simulate")}
              </a>
            )}
          </span>
        )}
      </div>
      {editing ? (
        <div className="flex gap-2">
          <button onClick={submit} disabled={reprocessing}
            className="bg-[#00E5FF] text-black px-3 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-white disabled:opacity-40"
            style={{ borderRadius: "2px" }} data-testid="ct-contract-meta-save">
            <Save className="w-3 h-3 inline mr-1" /> {reprocessing ? t("ct.contract.reprocessing") : t("common.save")}
          </button>
          <button onClick={() => { setEditing(false); setVal(contract.contract_value ?? ""); setName(contract.counterparty_name || ""); setCtype(contract.contract_type || "other"); }} className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">
            {t("common.cancel")}
          </button>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-[#A0AEC0] hover:text-[#00E5FF]" data-testid="ct-contract-meta-edit">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function NegotiationImpactSection({ clauses, selectedClauseId, onSelect, impact }) {
  const { t } = useLanguage();
  if (clauses.length === 0) return null;
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] p-4 mt-4" style={{ borderRadius: "2px" }} data-testid="ct-negotiation-section">
      <div className="flex items-center gap-2 mb-2">
        <GitBranch className="w-4 h-4 text-[#7C3AED]" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#7C3AED] font-bold">{t("ct.negotiation.title")}</p>
      </div>
      <p className="text-[11px] text-[#A0AEC0] mb-3">{t("ct.negotiation.desc")}</p>
      <select
        value={selectedClauseId || ""}
        onChange={e => e.target.value && onSelect(e.target.value)}
        className="bg-[#0A0C10] border border-white/[0.06] px-2 py-1.5 text-xs text-white w-full"
        style={{ borderRadius: "2px" }}
        data-testid="ct-negotiation-select"
      >
        <option value="">{t("ct.negotiation.select")}</option>
        {clauses.map(cl => (
          <option key={cl.id} value={cl.id}>
            [{cl.clause_type}] {(cl.trigger_condition || "").slice(0, 80)}
          </option>
        ))}
      </select>
      {impact && (
        <div className="mt-3" data-testid="ct-negotiation-result">
          {impact.linked_contracts.length === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic">{t("ct.negotiation.no_impact")}</p>
          ) : (
            <>
              <p className="text-[11px] text-white mb-2 font-medium">
                {impact.linked_contracts.length} {t("ct.negotiation.impacted")}
              </p>
              <ul className="space-y-1.5">
                {impact.linked_contracts.map(lc => (
                  <li key={lc.id} className="flex items-center gap-2 text-xs">
                    <Link2 className="w-3 h-3 text-[#7C3AED]" />
                    <span className="text-white font-medium">{lc.filename || lc.id.slice(0, 8)}</span>
                    {lc.counterparty_name && <span className="text-[#A0AEC0]">· {lc.counterparty_name}</span>}
                    {lc.contract_value != null && <span className="text-[#00E5FF] tabular-nums font-bold ml-auto">€{Number(lc.contract_value).toLocaleString("it-IT")}</span>}
                    <span className="text-[9px] text-[#A0AEC0] uppercase tracking-widest">{lc.hops} {t("ct.negotiation.hops")}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ClauseCard({ clause, labels, onEdit, contracts = [] }) {
  const { t } = useLanguage();
  const needsReview = clause.review_status === "needs_review" || clause.confidence_score < 0.7;
  const linkedContract = clause.linked_contract_id ? contracts.find(c => c.id === clause.linked_contract_id) : null;
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] p-3" style={{ borderRadius: "2px" }} data-testid={`ct-clause-${clause.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {needsReview && (
            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#FFB800]/15 text-[#FFB800]" data-testid={`ct-badge-review-${clause.id}`}>
              {t("ct.badge.review")}
            </span>
          )}
          {!needsReview && (
            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#00E676]/15 text-[#00E676]" data-testid={`ct-badge-verified-${clause.id}`}>
              {t("ct.badge.verified")}
            </span>
          )}
          <span className="text-[9px] text-[#A0AEC0] tracking-widest">
            {t("ct.field.confidence")}: <span className="font-bold tabular-nums text-white">{(clause.confidence_score * 100).toFixed(0)}%</span>
          </span>
          {clause.deadline_date && (
            <span className="text-[9px] text-[#A0AEC0] tracking-widest">
              {t("ct.field.deadline")}: <span className="text-white">{clause.deadline_date}</span>
            </span>
          )}
        </div>
        <button onClick={onEdit} className="text-[#A0AEC0] hover:text-[#00E5FF] flex-shrink-0" title={t("ct.edit.tooltip")} data-testid={`ct-clause-edit-${clause.id}`}>
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-sm text-white mb-1"><span className="text-[#A0AEC0] text-xs">{t("ct.field.condition")}: </span>{clause.trigger_condition}</p>
      <p className="text-sm text-white mb-2"><span className="text-[#A0AEC0] text-xs">{t("ct.field.consequence")}: </span>{clause.consequence}</p>
      <div className="flex items-center gap-3 mb-2 text-[10px] text-[#A0AEC0]">
        <span>{t("ct.field.trigger")}: <span className="text-white">{humanLabel(t, "variable", clause.trigger_variable)}</span></span>
        {clause.threshold_value !== null && clause.threshold_value !== undefined && (
          <span>{t("ct.field.threshold")}: <span className="text-white font-mono">{clause.threshold_value}</span></span>
        )}
      </div>
      {clause.clause_type === "parity_clause" && (
        <div className="mt-2 flex items-center gap-2 text-[11px]">
          <Link2 className="w-3.5 h-3.5 text-[#00E676]" />
          {linkedContract ? (
            <span className="text-white">
              {t("ct.contract.linked_to")}: <span className="font-bold text-[#00E676]">{linkedContract.filename}</span>
              {linkedContract.counterparty_name && <span className="text-[#A0AEC0]"> · {linkedContract.counterparty_name}</span>}
            </span>
          ) : (
            <span className="text-[#FFB800] italic" data-testid={`ct-clause-nolink-${clause.id}`}>
              {t("ct.contract.no_link")}
              {clause.linked_contract_hint && <span className="ml-1">({t("ct.field.llm_hint")}: {clause.linked_contract_hint.slice(0, 60)})</span>}
            </span>
          )}
        </div>
      )}
      {clause.source_text && (
        <div className="mt-2 p-2 bg-[#0A0C10] border-l-2 text-[11px] text-[#CBD5E1] italic" style={{ borderColor: CLAUSE_COLORS[clause.clause_type] }}>
          &ldquo;{clause.source_text}&rdquo;
        </div>
      )}
    </div>
  );
}

function ClauseEditor({ clause, labels, onCancel, onSave, otherContracts = [] }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    clause_type: clause.clause_type,
    trigger_condition: clause.trigger_condition || "",
    trigger_variable: clause.trigger_variable || "other",
    threshold_value: clause.threshold_value ?? "",
    deadline_date: clause.deadline_date || "",
    consequence: clause.consequence || "",
    source_text: clause.source_text || "",
    linked_contract_id: clause.linked_contract_id || "",
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    const patch = { ...form };
    patch.threshold_value = patch.threshold_value === "" ? null : parseFloat(patch.threshold_value);
    if (!patch.deadline_date) patch.deadline_date = null;
    // Only send linked_contract_id when parity_clause; empty → null (removes link)
    if (patch.clause_type !== "parity_clause") {
      delete patch.linked_contract_id;
    } else {
      patch.linked_contract_id = patch.linked_contract_id || "";
    }
    onSave(patch);
  };

  return (
    <div className="bg-[#0D0F12] border border-[#00E5FF]/40 p-3 space-y-2" style={{ borderRadius: "2px" }} data-testid={`ct-clause-editor-${clause.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <Field label={t("ct.field.type")}>
          <select value={form.clause_type} onChange={(e) => update("clause_type", e.target.value)}
            className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
            data-testid={`ct-edit-type-${clause.id}`}>
            {Object.keys(labels).map(t => <option key={t} value={t}>{labels[t]}</option>)}
          </select>
        </Field>
        <Field label={t("ct.field.trigger_variable")}>
          <select value={form.trigger_variable} onChange={(e) => update("trigger_variable", e.target.value)}
            className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
            data-testid={`ct-edit-trigvar-${clause.id}`}>
            {TRIGGER_VAR_OPTIONS.map(tv => <option key={tv} value={tv}>{humanLabel(t, "variable", tv)}</option>)}
          </select>
        </Field>
        <Field label={t("ct.field.threshold")}>
          <input type="number" step="0.01" value={form.threshold_value} onChange={(e) => update("threshold_value", e.target.value)}
            className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
            data-testid={`ct-edit-threshold-${clause.id}`} />
        </Field>
        <Field label={t("ct.field.deadline_placeholder")}>
          <input type="text" placeholder="YYYY-MM-DD" value={form.deadline_date} onChange={(e) => update("deadline_date", e.target.value)}
            className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
            data-testid={`ct-edit-deadline-${clause.id}`} />
        </Field>
      </div>
      <Field label={t("ct.field.condition")}>
        <textarea value={form.trigger_condition} onChange={(e) => update("trigger_condition", e.target.value)} rows={2}
          className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
          data-testid={`ct-edit-condition-${clause.id}`} />
      </Field>
      <Field label={t("ct.field.consequence")}>
        <textarea value={form.consequence} onChange={(e) => update("consequence", e.target.value)} rows={2}
          className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
          data-testid={`ct-edit-consequence-${clause.id}`} />
      </Field>
      <Field label={t("ct.field.source_text")}>
        <textarea value={form.source_text} onChange={(e) => update("source_text", e.target.value)} rows={2}
          className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white italic" style={{ borderRadius: "2px" }}
          data-testid={`ct-edit-source-${clause.id}`} />
      </Field>
      {form.clause_type === "parity_clause" && (
        <Field label={t("ct.contract.linked_to")}>
          <select
            value={form.linked_contract_id || ""}
            onChange={e => update("linked_contract_id", e.target.value)}
            className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white"
            style={{ borderRadius: "2px" }}
            data-testid={`ct-edit-linked-${clause.id}`}
          >
            <option value="">{t("ct.contract.select_linked")}</option>
            {otherContracts.map(oc => (
              <option key={oc.id} value={oc.id}>
                {oc.filename} {oc.counterparty_name ? `· ${oc.counterparty_name}` : ""}
              </option>
            ))}
          </select>
          {clause.linked_contract_hint && (
            <p className="text-[10px] text-[#FFB800] mt-1 italic">
              {t("ct.field.llm_hint")}: {clause.linked_contract_hint}
            </p>
          )}
        </Field>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="text-[10px] uppercase tracking-widest text-[#A0AEC0] hover:text-white px-3 py-1.5 flex items-center gap-1" data-testid={`ct-edit-cancel-${clause.id}`}>
          <X className="w-3 h-3" /> {t("ct.edit.cancel")}
        </button>
        <button onClick={handleSave} className="text-[10px] uppercase tracking-widest bg-[#00E5FF] text-black font-bold px-3 py-1.5 hover:bg-white transition-colors flex items-center gap-1" style={{ borderRadius: "2px" }} data-testid={`ct-edit-save-${clause.id}`}>
          <Save className="w-3 h-3" /> {t("ct.edit.save")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] text-[#A0AEC0] tracking-widest uppercase">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────
   WHAT-IF PANEL — multi-variable scenarios + presets
   ───────────────────────────────────────────────────────────── */
function WhatIfPanel({ labels, onOpenContract }) {
  const { t } = useLanguage();
  const [sources, setSources] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState({}); // {source_name: value}
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/external-sources`, { withCredentials: true }).then(r => setSources(r.data)).catch(() => {});
    axios.get(`${API}/scenarios/presets`, { withCredentials: true }).then(r => setPresets(r.data)).catch(() => {});
  }, []);

  const applyPreset = (preset) => {
    const s = {};
    for (const v of preset.variables) s[v.name] = v.hypothetical_value;
    setSelected(s);
  };
  const toggleVar = (name, defaultValue) => {
    setSelected(prev => {
      const next = { ...prev };
      if (name in next) delete next[name]; else next[name] = defaultValue;
      return next;
    });
  };

  const run = async () => {
    setRunning(true);
    try {
      const variables = Object.entries(selected).map(([name, hypothetical_value]) => ({ name, hypothetical_value: parseFloat(hypothetical_value) }));
      const r = await axios.post(`${API}/whatif`, { variables }, { withCredentials: true });
      setResult(r.data);
    } catch { /* noop */ } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="ct-whatif-panel">
      {/* Presets */}
      <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">{t("ct.whatif.presets")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {presets.map(p => (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className="text-left bg-[#0A0C10] border border-white/[0.04] hover:border-[#00E5FF]/40 p-3 transition-colors"
              style={{ borderRadius: "2px" }}
              data-testid={`ct-preset-${p.id}`}
            >
              <p className="text-sm font-bold text-white">{p.name}</p>
              <p className="text-[10px] text-[#A0AEC0] mt-1">{p.description}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {p.variables.map(v => (
                  <span key={v.name} className="text-[9px] bg-[#00E5FF]/12 text-[#00E5FF] px-1.5 py-0.5" style={{ borderRadius: "2px" }}>
                    {humanLabel(t, "variable", v.name)}: {v.hypothetical_value}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Variable selector */}
      <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-3">{t("ct.whatif.compose")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {sources.map(s => {
            const active = s.name in selected;
            return (
              <div key={s.name} className={`border p-3 ${active ? "border-[#00E5FF]/60 bg-[#00E5FF]/6" : "border-white/[0.04]"}`} style={{ borderRadius: "2px" }}>
                <div className="flex items-start gap-2">
                  <input type="checkbox" checked={active} onChange={() => toggleVar(s.name, s.current_value)} className="mt-1" data-testid={`ct-whatif-toggle-${s.name}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white">{humanLabel(t, "variable", s.name)}</p>
                    <p className="text-[10px] text-[#A0AEC0]">{s.description}</p>
                    <p className="text-[10px] text-[#A0AEC0] mt-1">{t("ct.whatif.current")}: <span className="text-white font-mono">{s.current_value}{s.unit}</span></p>
                  </div>
                </div>
                {active && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      step="0.01"
                      value={selected[s.name]}
                      onChange={e => setSelected(prev => ({ ...prev, [s.name]: e.target.value }))}
                      className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white"
                      style={{ borderRadius: "2px" }}
                      data-testid={`ct-whatif-input-${s.name}`}
                    />
                    <span className="text-[10px] text-[#A0AEC0]">{s.unit}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={run}
            disabled={Object.keys(selected).length === 0 || running}
            className="bg-[#00E5FF] text-black px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-40"
            style={{ borderRadius: "2px" }}
            data-testid="ct-whatif-run"
          >
            {running ? t("ct.whatif.running") : t("ct.whatif.run")}
          </button>
          {Object.keys(selected).length > 0 && (
            <button onClick={() => setSelected({})} className="text-[10px] text-[#A0AEC0] hover:text-white tracking-widest uppercase" data-testid="ct-whatif-clear">
              {t("common.reset")}
            </button>
          )}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid="ct-whatif-result">
          {/* Total exposure prominent */}
          <div className="border-l-4 border-[#FF1744] bg-[#FF1744]/8 pl-4 pr-3 py-3 mb-4">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#FF1744] font-bold mb-1">{t("ct.whatif.total_exposure")}</p>
            <p className="text-3xl font-bold tabular-nums text-white" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="ct-whatif-exposure">
              €{Number(result.totals.total_exposure_eur).toLocaleString("it-IT")}
            </p>
            <p className="text-[11px] text-[#CBD5E1] mt-1">
              {result.totals.contracts_impacted} {t("ct.whatif.impacted")} · {result.totals.contracts_direct} {t("ct.whatif.direct")} · {result.totals.contracts_indirect} {t("ct.whatif.indirect")}
              {result.totals.contracts_without_value > 0 && (
                <span className="text-[#FFB800]"> · {result.totals.contracts_without_value} {t("ct.whatif.no_value")}</span>
              )}
            </p>
          </div>

          <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold mb-2">{t("ct.whatif.contracts_list")}</p>
          <div className="space-y-2">
            {result.contracts.map(c => (
              <div key={c.contract_id} className="border border-white/[0.04] p-3" style={{ borderRadius: "2px" }} data-testid={`ct-whatif-contract-${c.contract_id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => onOpenContract(c.contract_id)} className="text-sm font-bold text-white hover:text-[#00E5FF]">
                        {c.filename || c.contract_id.slice(0, 8)}
                      </button>
                      {c.is_direct ? (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#FF1744]/15 text-[#FF1744]" style={{ borderRadius: "2px" }}>{t("ct.whatif.direct_badge")}</span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#7C3AED]/15 text-[#B39BFF]" style={{ borderRadius: "2px" }} data-testid={`ct-whatif-indirect-${c.contract_id}`}>{t("ct.whatif.indirect_badge")}</span>
                      )}
                      {c.counterparty_name && <span className="text-[10px] text-[#A0AEC0]">· {c.counterparty_name}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {c.triggered_by.map(v => (
                        <span key={v} className="text-[9px] font-mono bg-[#FFB800]/15 text-[#FFB800] px-1.5 py-0.5" style={{ borderRadius: "2px" }}>
                          via {humanLabel(t, "variable", v)}
                        </span>
                      ))}
                    </div>
                    {/* Chain path if indirect */}
                    {!c.is_direct && Object.entries(c.path_by_variable).map(([varName, path]) => (
                      path.length > 1 && (
                        <p key={varName} className="text-[10px] text-[#A0AEC0] mt-1 font-mono">
                          <Network className="w-3 h-3 inline mr-1" />
                          {path.map((p, i) => (
                            <span key={i}>
                              {i > 0 && " → "}
                              <span className={i === path.length - 1 ? "text-[#B39BFF]" : "text-white"}>{p.slice(0, 8)}</span>
                            </span>
                          ))}
                        </p>
                      )
                    ))}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {c.contract_value != null ? (
                      <p className="text-sm font-bold tabular-nums text-[#00E5FF]" style={{ fontFamily: "Chivo, sans-serif" }}>
                        €{Number(c.contract_value).toLocaleString("it-IT")}
                      </p>
                    ) : (
                      <p className="text-[10px] text-[#FFB800] italic">{t("ct.whatif.no_value_short")}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {result.contracts.length === 0 && (
              <p className="text-[11px] text-[#6B7280] py-6 text-center">{t("ct.whatif.empty")}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   COUNTERPARTIES PANEL
   ───────────────────────────────────────────────────────────── */
function CounterpartiesPanel({ onOpenContract }) {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [registry, setRegistry] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, reg] = await Promise.all([
        axios.get(`${API}/counterparties`, { withCredentials: true }),
        axios.get(`${API}/counterparty-registry`, { withCredentials: true }),
      ]);
      setItems(r.data);
      setRegistry(reg.data);
    } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ensureRegistry = async (name) => {
    let entry = registry.find(x => x.name === name);
    if (!entry) {
      try {
        const r = await axios.post(`${API}/counterparty-registry`, { name }, { withCredentials: true });
        entry = r.data;
        setRegistry(prev => [...prev, entry]);
      } catch { return null; }
    }
    return entry;
  };

  const setRisk = async (name, cp_id, risk_status) => {
    let id = cp_id;
    if (!id) {
      const entry = await ensureRegistry(name);
      if (!entry) return;
      id = entry.id;
    }
    try {
      await axios.patch(`${API}/counterparty-registry/${id}`, { risk_status }, { withCredentials: true });
      await load();
    } catch { /* noop */ }
  };

  if (items === null) return <p className="text-[11px] text-[#6B7280] py-8 text-center">{t("ct.loading")}</p>;
  if (items.length === 0) return <p className="text-[11px] text-[#6B7280] py-8 text-center">{t("ct.counterparties.empty")}</p>;

  const RISK_COLORS = { stable: "#00E676", watch: "#FFB800", distressed: "#FF7043", default: "#FF1744", unmonitored: "#6B7280" };
  const RISK_KEYS = ["stable", "watch", "distressed", "default"];

  return (
    <div className="space-y-3" data-testid="ct-counterparties-panel">
      {items.map(cp => (
        <div key={cp.counterparty_name} className="bg-[#0D0F12] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }} data-testid={`ct-cp-${cp.counterparty_name}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Users className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
                <p className="text-base font-bold text-white truncate">{cp.counterparty_name}</p>
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" style={{ backgroundColor: `${RISK_COLORS[cp.risk_status]}22`, color: RISK_COLORS[cp.risk_status], borderRadius: "2px" }} data-testid={`ct-cp-risk-${cp.counterparty_name}`}>
                  {t(`ct.cp_risk.${cp.risk_status}`)}
                </span>
                {cp.concentration_variable && (
                  <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#FF1744]/15 text-[#FF1744]" style={{ borderRadius: "2px" }} data-testid={`ct-cp-concentration-${cp.counterparty_name}`}>
                    <AlertTriangle className="w-3 h-3 inline mr-0.5" /> {t("ct.counterparties.concentration")} {humanLabel(t, "variable", cp.concentration_variable)}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-[#A0AEC0] mt-1">
                {cp.contracts_count} {t("ct.alerts.contracts")} · {t("ct.counterparties.exposed_to")}: {cp.exposed_variables.map(v => (
                  <span key={v} className="text-white">{humanLabel(t, "variable", v)} </span>
                ))}
              </p>
              {/* Risk quick-set buttons */}
              <div className="flex items-center gap-1 mt-2">
                {RISK_KEYS.map(rk => (
                  <button key={rk} onClick={() => setRisk(cp.counterparty_name, cp.counterparty_id, rk)}
                    className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 border transition-colors ${cp.risk_status === rk ? "border-[#00E5FF]" : "border-white/[0.06] hover:border-white/[0.15]"}`}
                    style={{ color: RISK_COLORS[rk], borderRadius: "2px" }}
                    data-testid={`ct-cp-setrisk-${cp.counterparty_name}-${rk}`}
                  >
                    {t(`ct.cp_risk.${rk}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] tracking-widest uppercase text-[#A0AEC0]">{t("ct.counterparties.total_value")}</p>
              <p className="text-xl font-bold tabular-nums text-[#00E5FF]" style={{ fontFamily: "Chivo, sans-serif" }}>
                €{Number(cp.total_value_eur).toLocaleString("it-IT")}
              </p>
            </div>
          </div>
          <div className="border-t border-white/[0.04] pt-2">
            <ul className="text-xs space-y-1">
              {cp.contracts.map(c => (
                <li key={c.id} className="flex items-center justify-between">
                  <button onClick={() => onOpenContract(c.id)} className="text-white hover:text-[#00E5FF] flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {c.filename || c.id.slice(0, 8)}
                  </button>
                  <span className="text-[#A0AEC0] tabular-nums">{c.contract_value != null ? `€${Number(c.contract_value).toLocaleString("it-IT")}` : "—"}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   PREDICTIONS PANEL (linear regression on source history)
   ───────────────────────────────────────────────────────────── */
function PredictionsPanel({ labels, onOpenContract }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [actions, setActions] = useState({});
  const [users, setUsers] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [pRes, aRes, uRes] = await Promise.all([
        axios.get(`${API}/predictions/timeline`, { withCredentials: true }),
        axios.get(`${API}/timeline-actions`, { withCredentials: true }),
        axios.get(`${API}/tenant-users`, { withCredentials: true }),
      ]);
      setData(pRes.data);
      setActions(aRes.data || {});
      setUsers(uRes.data || []);
    } catch {
      setData({ predictions: [], insufficient_history_sources: [] });
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveAction = async (clauseId, payload) => {
    try {
      const r = await axios.put(`${API}/timeline-actions/${clauseId}`, payload, { withCredentials: true });
      setActions(prev => ({ ...prev, [clauseId]: r.data }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    }
  };

  if (data === null) return <p className="text-[11px] text-[#6B7280] py-8 text-center">{t("ct.loading")}</p>;

  return (
    <div className="space-y-3" data-testid="ct-predictions-panel">
      <div className="bg-[#FFB800]/8 border border-[#FFB800]/30 p-3" style={{ borderRadius: "2px" }}>
        <p className="text-[11px] text-[#FFB800]">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          {data.disclaimer}
        </p>
      </div>

      {data.insufficient_history_sources.length > 0 && (
        <div className="bg-[#0D0F12] border border-white/[0.04] p-3" style={{ borderRadius: "2px" }}>
          <p className="text-[10px] text-[#A0AEC0]">
            {t("ct.predictions.insufficient")}: {data.insufficient_history_sources.map(s => <span key={s} className="text-white mr-2">{humanLabel(t, "variable", s)}</span>)}
          </p>
        </div>
      )}

      {data.predictions.length === 0 ? (
        <p className="text-[11px] text-[#6B7280] py-8 text-center">{t("ct.predictions.empty")}</p>
      ) : (
        <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] tracking-widest uppercase text-[#6B7280] border-b border-white/[0.04]">
                <th className="text-left py-2 px-3">{t("ct.predictions.eta")}</th>
                <th className="text-left py-2 px-2">{t("ct.predictions.reneg_window")}</th>
                <th className="text-left py-2 px-3">{t("ct.list.filename")}</th>
                <th className="text-left py-2 px-2">{t("ct.field.type")}</th>
                <th className="text-left py-2 px-2">Trigger</th>
                <th className="text-right py-2 px-2">{t("ct.field.threshold")}</th>
                <th className="text-right py-2 px-3">{t("ct.predictions.days")}</th>
                <th className="text-left py-2 px-2">{t("ct.timeline.owner")}</th>
                <th className="text-left py-2 px-2">{t("ct.timeline.status")}</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.predictions.map(p => (
                <PredictionRow key={p.clause_id} p={p} labels={labels} action={actions[p.clause_id]} users={users} onSaveAction={saveAction} onOpenContract={onOpenContract} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   NL Query Bar (P3)
   ───────────────────────────────────────────────────────────── */
function NLQueryBar({ onOpenContract }) {
  const { t } = useLanguage();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/nl-query`, { query: q }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setResult({ intent: "unknown", message: e?.response?.data?.detail || t("ct.nlquery.unknown") });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-gradient-to-r from-[#7C3AED]/12 to-[#00E5FF]/8 border border-[#7C3AED]/25 p-4" style={{ borderRadius: "2px" }} data-testid="ct-nlquery-bar">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#7C3AED]" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#B39BFF] font-bold">{t("ct.nlquery.title")}</p>
      </div>
      <div className="flex gap-2">
        <input
          type="text" value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && ask()}
          placeholder={t("ct.nlquery.placeholder")}
          className="flex-1 bg-[#0A0C10] border border-white/[0.06] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#7C3AED]"
          style={{ borderRadius: "2px" }}
          data-testid="ct-nlquery-input"
        />
        <button onClick={ask} disabled={loading || !q.trim()}
          className="bg-[#7C3AED] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:bg-[#B39BFF] disabled:opacity-40 flex items-center gap-1"
          style={{ borderRadius: "2px" }}
          data-testid="ct-nlquery-submit"
        >
          <Send className="w-3 h-3" /> {loading ? "…" : t("ct.nlquery.button")}
        </button>
      </div>
      {result && (
        <div className="mt-3" data-testid="ct-nlquery-result">
          {result.intent === "unknown" ? (
            <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-2 text-[11px] text-[#FFB800]" style={{ borderRadius: "2px" }}>
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {result.message}
            </div>
          ) : (
            <div className="text-[11px] space-y-1">
              <p className="text-[#A0AEC0]">
                {t("ct.nlquery.interpreted_as")}: <span className="text-[#7C3AED] font-bold">{humanLabel(t, "intent", result.intent)}</span>
                {result.interpreted?.counterparty_name && <span className="text-white"> · {result.interpreted.counterparty_name}</span>}
                {result.interpreted?.variable_name && <span className="text-white"> · {humanLabel(t, "variable", result.interpreted.variable_name)}</span>}
                {result.interpreted?.hypothetical_value !== null && result.interpreted?.hypothetical_value !== undefined && <span className="text-white"> · {result.interpreted.hypothetical_value}</span>}
                <span className="text-[#A0AEC0] ml-2">({Math.round((result.confidence || 0) * 100)}%)</span>
              </p>
              {result.result?.totals && (
                <div className="border-l-2 border-[#7C3AED] pl-3 py-1">
                  <p className="text-white">
                    <span className="font-bold tabular-nums text-[#FF1744]">€{Number(result.result.totals.total_exposure_eur || 0).toLocaleString("it-IT")}</span>
                    {" · "}
                    <span>{result.result.totals.contracts_impacted} {t("ct.nlquery.impacted")} ({result.result.totals.contracts_direct} {t("ct.whatif.direct")} + {result.result.totals.contracts_indirect} {t("ct.whatif.indirect")})</span>
                  </p>
                  {result.result.contracts && (
                    <ul className="mt-1 space-y-0.5">
                      {result.result.contracts.slice(0, 5).map(c => (
                        <li key={c.id} className="text-[10px]">
                          <button onClick={() => onOpenContract(c.id)} className="text-[#00E5FF] hover:text-white">
                            {c.filename || c.id.slice(0, 8)}
                          </button>
                          {c.contract_value != null && <span className="text-[#A0AEC0] tabular-nums"> · €{Number(c.contract_value).toLocaleString("it-IT")}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Report Download Button (P2 slot)
   ───────────────────────────────────────────────────────────── */
function ReportDownloadButton() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/report/portfolio.pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `contract-twin-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  return (
    <button onClick={download} disabled={loading}
      className="bg-transparent border border-[#00E5FF]/40 text-[#00E5FF] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-[#00E5FF]/8 disabled:opacity-40 flex items-center gap-1.5"
      style={{ borderRadius: "2px" }}
      data-testid="ct-report-download"
    >
      <Download className="w-3.5 h-3.5" /> {loading ? t("ct.report.generating") : t("ct.report.generate")}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
   Recommendations Panel (P5)
   ───────────────────────────────────────────────────────────── */
function RecommendationsPanel() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recommendations`, { withCredentials: true });
      setItems(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/recommendations/generate`, {}, { withCredentials: true });
      await load();
    } catch { /* noop */ } finally { setGenerating(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/recommendations/${id}`, { status }, { withCredentials: true });
      await load();
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, [load]);

  const active = items.filter(x => x.status === "active");
  const archived = items.filter(x => x.status !== "active");

  return (
    <div className="space-y-3" data-testid="ct-recommendations-panel">
      <div className="flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-[#FFB800]" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#FFB800] font-bold">{t("ct.reco.title")}</p>
        <button onClick={generate} disabled={generating}
          className="ml-auto bg-[#FFB800] text-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-white disabled:opacity-40 flex items-center gap-1.5"
          style={{ borderRadius: "2px" }}
          data-testid="ct-reco-generate"
        >
          <Sparkles className="w-3 h-3" /> {generating ? "…" : t("ct.reco.generate")}
        </button>
      </div>
      {loading && <p className="text-[11px] text-[#6B7280]">{t("ct.loading")}</p>}
      {!loading && active.length === 0 && (
        <p className="text-[11px] text-[#6B7280] text-center py-8">{t("ct.reco.empty")}</p>
      )}
      {active.map(r => (
        <div key={r.id} className="bg-[#0D0F12] border-l-4 border-[#FFB800] border-t border-r border-b border-white/[0.04] p-3" style={{ borderRadius: "2px" }} data-testid={`ct-reco-${r.id}`}>
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] text-white">{humanizeText(t, r.text)}</p>
              <p className="text-[10px] text-[#A0AEC0] mt-1">
                <span className="text-white font-bold">{r.counterparty_name}</span> · <span className="text-[#B39BFF]">{humanLabel(t, "variable", r.variable_name)}</span> · {r.contracts_count} {t("ct.alerts.contracts")} · <span className="tabular-nums text-[#FFB800]">€{Number(r.exposure_eur).toLocaleString("it-IT")}</span>
              </p>
              <p className="text-[9px] text-[#6B7280] italic mt-1">{t("ct.reco.disclaimer")}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => setStatus(r.id, "considered")} className="text-[9px] text-[#00E676] bg-[#00E676]/12 hover:bg-[#00E676]/20 px-2 py-0.5 font-bold uppercase tracking-widest" style={{ borderRadius: "2px" }} data-testid={`ct-reco-consider-${r.id}`}>
                <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> {t("ct.reco.consider")}
              </button>
              <button onClick={() => setStatus(r.id, "dismissed")} className="text-[9px] text-[#A0AEC0] bg-white/[0.04] hover:bg-white/[0.08] px-2 py-0.5 font-bold uppercase tracking-widest" style={{ borderRadius: "2px" }} data-testid={`ct-reco-dismiss-${r.id}`}>
                <X className="w-3 h-3 inline mr-0.5" /> {t("ct.reco.dismiss")}
              </button>
            </div>
          </div>
        </div>
      ))}
      {archived.length > 0 && (
        <details className="pt-4">
          <summary className="text-[10px] tracking-widest uppercase text-[#A0AEC0] cursor-pointer">{t("ct.reco.archive")} ({archived.length})</summary>
          <ul className="mt-2 space-y-1">
            {archived.map(r => (
              <li key={r.id} className="text-[10px] text-[#6B7280] flex items-center gap-2 py-1 border-b border-white/[0.03]" data-testid={`ct-reco-archived-${r.id}`}>
                <span className={`font-bold ${r.status === "considered" ? "text-[#00E676]" : "text-[#A0AEC0]"}`}>[{humanLabel(t, "reco_status", r.status)}]</span>
                <span className="flex-1 truncate">{r.text}</span>
                <button onClick={() => setStatus(r.id, "active")} className="text-[9px] uppercase tracking-widest text-[#00E5FF] hover:text-white" data-testid={`ct-reco-reopen-${r.id}`}>{t("ct.reco.reopen")}</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   PredictionRow with reneg window + collaborative action (P6+iter24)
   ───────────────────────────────────────────────────────────── */
function PredictionRow({ p, labels, action, users, onSaveAction, onOpenContract }) {
  const { t } = useLanguage();
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [editing, setEditing] = useState(false);
  const [ownerId, setOwnerId] = useState(action?.owner_user_id || "");
  const [status, setStatus] = useState(action?.status || "todo");
  const [notes, setNotes] = useState(action?.notes || "");

  React.useEffect(() => {
    setOwnerId(action?.owner_user_id || "");
    setStatus(action?.status || "todo");
    setNotes(action?.notes || "");
  }, [action]);

  const askWhy = async () => {
    setExplaining(true);
    try {
      const r = await axios.post(`${API}/predictions/explain`, { clause_id: p.clause_id }, { withCredentials: true });
      setExplanation(r.data);
    } catch { /* noop */ } finally { setExplaining(false); }
  };

  const save = async () => {
    const owner = users.find(u => u.id === ownerId);
    await onSaveAction(p.clause_id, {
      owner_user_id: ownerId || null,
      owner_email: owner?.email || null,
      status,
      notes,
    });
    setEditing(false);
  };

  const statusColors = {
    todo: "text-[#A0AEC0] bg-white/[0.04]",
    in_progress: "text-[#00E5FF] bg-[#00E5FF]/12",
    done: "text-[#00E676] bg-[#00E676]/12",
    deferred: "text-[#FFB800] bg-[#FFB800]/12",
  };

  return (
    <>
      <tr className="border-t border-white/[0.03]" data-testid={`ct-pred-${p.clause_id}`}>
        <td className="py-2 px-3 text-white font-bold">{p.estimated_activation_date}</td>
        <td className="py-2 px-2">
          <span className="text-[10px] bg-[#00E676]/12 text-[#00E676] px-1.5 py-0.5 font-mono" style={{ borderRadius: "2px" }} data-testid={`ct-pred-reneg-${p.clause_id}`}>
            {p.renegotiation_window_start || "—"}
          </span>
        </td>
        <td className="py-2 px-3">
          <button onClick={() => onOpenContract(p.contract_id)} className="text-white hover:text-[#00E5FF]">{p.filename}</button>
          {p.counterparty_name && <div className="text-[10px] text-[#A0AEC0]">{p.counterparty_name}</div>}
        </td>
        <td className="py-2 px-2" style={{ color: CLAUSE_COLORS[p.clause_type] }}>{labels[p.clause_type]}</td>
        <td className="py-2 px-2 text-white text-[10px]">{humanLabel(t, "variable", p.trigger_variable)}<br /><span className="text-[#A0AEC0]">{p.current_value}</span></td>
        <td className="py-2 px-2 text-right tabular-nums text-[#FFB800]">{p.threshold_value}</td>
        <td className="py-2 px-3 text-right tabular-nums text-[#00E5FF] font-bold">{p.eta_days}</td>
        <td className="py-2 px-2 text-[10px]" data-testid={`ct-pred-owner-${p.clause_id}`}>
          {action?.owner_email ? (
            <span className="text-white">{action.owner_email}</span>
          ) : (
            <span className="text-[#6B7280] italic">{t("ct.timeline.no_owner")}</span>
          )}
        </td>
        <td className="py-2 px-2">
          <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-widest ${statusColors[action?.status || "todo"]}`} style={{ borderRadius: "2px" }} data-testid={`ct-pred-status-${p.clause_id}`}>
            {t(`ct.timeline.status.${action?.status || "todo"}`)}
          </span>
        </td>
        <td className="py-2 px-2 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => setEditing(v => !v)} className="text-[9px] text-[#00E5FF] hover:text-white uppercase tracking-widest" data-testid={`ct-pred-edit-${p.clause_id}`}>
              {t("ct.timeline.edit")}
            </button>
            <button onClick={askWhy} disabled={explaining} className="text-[9px] text-[#B39BFF] hover:text-white flex items-center gap-1 uppercase tracking-widest" data-testid={`ct-pred-why-${p.clause_id}`}>
              <HelpCircle className="w-3 h-3" /> {explaining ? "…" : t("ct.predictions.why")}
            </button>
          </div>
        </td>
      </tr>
      {editing && (
        <tr className="bg-[#00E5FF]/6">
          <td colSpan={10} className="px-4 py-3 border-t border-[#00E5FF]/20">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
              <div>
                <label className="text-[9px] tracking-widest uppercase text-[#6B7280] block mb-1">{t("ct.timeline.owner")}</label>
                <select value={ownerId} onChange={e => setOwnerId(e.target.value)}
                  className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
                  data-testid={`ct-pred-owner-select-${p.clause_id}`}>
                  <option value="">— {t("ct.timeline.no_owner")} —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.email}{u.name ? ` (${u.name})` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[9px] tracking-widest uppercase text-[#6B7280] block mb-1">{t("ct.timeline.status")}</label>
                <select value={status} onChange={e => setStatus(e.target.value)}
                  className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
                  data-testid={`ct-pred-status-select-${p.clause_id}`}>
                  {["todo","in_progress","done","deferred"].map(s => <option key={s} value={s}>{t(`ct.timeline.status.${s}`)}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[9px] tracking-widest uppercase text-[#6B7280] block mb-1">{t("ct.timeline.notes")}</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  rows={2} maxLength={2000}
                  className="w-full bg-[#0A0C10] border border-white/[0.06] px-2 py-1 text-xs text-white" style={{ borderRadius: "2px" }}
                  data-testid={`ct-pred-notes-${p.clause_id}`} />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={save}
                className="bg-[#00E5FF] text-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-white"
                style={{ borderRadius: "2px" }}
                data-testid={`ct-pred-save-${p.clause_id}`}>
                {t("ct.timeline.save")}
              </button>
              <button onClick={() => setEditing(false)}
                className="text-[11px] text-[#A0AEC0] hover:text-white uppercase tracking-widest px-3 py-1.5"
                data-testid={`ct-pred-cancel-${p.clause_id}`}>
                {t("ct.timeline.cancel")}
              </button>
              {action?.updated_at && action?.updated_by && (
                <span className="text-[10px] text-[#6B7280] ml-auto">
                  {t("ct.timeline.saved_by")}: {action.updated_by} · {String(action.updated_at).slice(0, 16).replace("T", " ")}
                </span>
              )}
            </div>
            {action?.notes && !editing && <p className="text-[11px] text-[#CBD5E1] italic mt-2">{action.notes}</p>}
          </td>
        </tr>
      )}
      {!editing && action?.notes && (
        <tr>
          <td colSpan={10} className="px-4 py-1 text-[11px] text-[#CBD5E1] italic border-t border-white/[0.03]">
            <FileText className="w-3 h-3 inline mr-1 text-[#6B7280]" />
            {action.notes}
          </td>
        </tr>
      )}
      {explanation && (
        <tr className="bg-[#7C3AED]/6">
          <td colSpan={10} className="px-4 py-2 text-[11px] text-[#CBD5E1] italic border-t border-[#7C3AED]/20">
            <Lightbulb className="w-3.5 h-3.5 inline text-[#B39BFF] mr-1" />
            {explanation.explanation}
          </td>
        </tr>
      )}
    </>
  );
}
